/**
 * col-per-member-tiered-form (US-AIF.P1-0250-S01 · @UC9423 @UC9424) — FORM COL-PER-MEMBER + COL-TIERED.
 *
 * MỞ RỘNG mẫu COL-EQUAL / COL-OPEN-AMOUNT / COL-TARGET-SPLIT (col-equal-form.ts, collection-form-specs.ts)
 * sang 2 usecase thu dùng FIELD LIST (US-AIF.P1-0249), CÙNG engine collection (createWithMode):
 *  - COL-PER-MEMBER (mode `per_member`): mỗi thành viên một số riêng. Chuỗi field:
 *    [fund_picker(fundId) → text(title) → list[member_picker + money] (entries) → date(dueDate)].
 *    Engine: createWithMode(amountMode='per_member', entries: {groupMemberId, amountMinor}[]).
 *  - COL-TIERED (mode `tiered`): thu theo bậc. Chuỗi field:
 *    [fund_picker → text → list[text(label) + money] (tiers) → list[member_picker + single_choice(tierLabel)]
 *    (memberTierMap) → date(dueDate)]. Engine: createWithMode(amountMode='tiered', tiers[], memberTierMap).
 *
 * HÀM THUẦN (DP-3): KHÔNG DB, KHÔNG LLM, KHÔNG Date.now (trừ `now` inject cho test date-not-past).
 * Money = string minor VND (B1 — no-float, >0, ≤ trần BIGINT). fund_picker + member_picker options do
 * SERVICE nạp (quỹ/thành viên THẬT của user) — domain chỉ validate value ∈ options (chống IDOR ở LỚP domain).
 *
 * 🔴 COL-TIERED options-động (index §6): cột con single_choice(tierLabel) của field memberTierMap phải có
 * options = NHÃN các bậc VỪA NHẬP ở field `tiers` trước đó. Server HYDRATE options này TỪ formState khi dựng
 * field memberTierMap (`buildTierLabelOptionsFromState`) — user CHỌN nhãn từ danh sách bậc, KHÔNG tự gõ tay
 * (tránh lệch nhãn tier). Domain validate: mỗi tierLabel được map PHẢI ∈ tập nhãn tier đã định nghĩa.
 *
 * REUSE THUẦN: cùng guard money/title/date với COL-EQUAL/collection-form-specs (assertAdhocAmountPositive/
 * assertDueDateNotPast + trần BIGINT + trần title). Reuse validateListField (0249) cho rows. Reuse
 * buildPerMemberDues/buildTieredDues (collection-amount-mode) ở engine — KHÔNG nhân bản công thức tiền.
 */
import { DomainError } from "../errors.js";
import { assertDueDateNotPast } from "../adhoc-collection.js";
import { DEFAULT_GROUP_TIMEZONE } from "../collection-schedule.js";
import {
  decodeRows,
  validateFieldValue,
  validateListField,
  type ListRow,
  type SlotFieldSpec,
  type SlotOption,
  type FormState,
} from "./form-slot-filling.js";

/** intentCode COL-PER-MEMBER (khớp FORM_SPEC_CATALOG + capability-catalog + router). */
export const COL_PER_MEMBER_INTENT_CODE = "COL-PER-MEMBER";
/** intentCode COL-TIERED. */
export const COL_TIERED_INTENT_CODE = "COL-TIERED";

/** specVersion (tăng khi đổi chuỗi field — session lưu để phát hiện drift). */
export const COL_PER_MEMBER_SPEC_VERSION = 1;
export const COL_TIERED_SPEC_VERSION = 1;

/**
 * 🔴 Trần PG signed BIGINT + trần độ dài tiêu đề — NHÂN BẢN y hệt col-equal-form / collection-form-specs
 * + contracts ADHOC_PG_BIGINT_MAX / ADHOC_TITLE_MAX_LENGTH. Domain thuần KHÔNG dep @goppy/contracts ⇒ giữ
 * literal chung; đổi một bên PHẢI đổi mọi bên.
 */
export const COL_PMT_PG_BIGINT_MAX = 9223372036854775807n;
export const COL_PMT_TITLE_MAX_LENGTH = 100;

/** Key field list entries (per-member). */
export const COL_PER_MEMBER_ENTRIES_KEY = "entries";
/** Key field list tiers (tiered — định nghĩa bậc). */
export const COL_TIERED_TIERS_KEY = "tiers";
/** Key field list memberTierMap (tiered — gán member → bậc). */
export const COL_TIERED_MEMBER_TIER_MAP_KEY = "memberTierMap";

/** Thứ tự field COL-PER-MEMBER (tất-định — thứ tự hỏi). */
export const COL_PER_MEMBER_FIELD_ORDER = [
  "fundId",
  "title",
  COL_PER_MEMBER_ENTRIES_KEY,
  "dueDate",
] as const;

/** Thứ tự field COL-TIERED (tất-định — tier defs TRƯỚC member→tier map). */
export const COL_TIERED_FIELD_ORDER = [
  "fundId",
  "title",
  COL_TIERED_TIERS_KEY,
  COL_TIERED_MEMBER_TIER_MAP_KEY,
  "dueDate",
] as const;

/** Một entry per-member đã validate — nối vào createWithMode.entries. */
export interface ColPerMemberEntry {
  readonly groupMemberId: string;
  /** STRING số nguyên dương minor (B1 — engine parse → BigInt). */
  readonly amountMinor: string;
}

/** Payload COL-PER-MEMBER đã validate — nối vào createWithMode(amountMode='per_member'). */
export interface ColPerMemberPayload {
  readonly fundId: string;
  readonly title: string;
  readonly entries: readonly ColPerMemberEntry[];
  /** 'YYYY-MM-DD'. */
  readonly dueDate: string;
}

/** Một bậc thu đã validate. */
export interface ColTier {
  readonly label: string;
  /** STRING số nguyên dương minor. */
  readonly amountMinor: string;
}

/** Payload COL-TIERED đã validate — nối vào createWithMode(amountMode='tiered'). */
export interface ColTieredPayload {
  readonly fundId: string;
  readonly title: string;
  readonly tiers: readonly ColTier[];
  /** memberId → tierLabel (mọi label ∈ tiers). */
  readonly memberTierMap: Readonly<Record<string, string>>;
  /** 'YYYY-MM-DD'. */
  readonly dueDate: string;
}

// ─── Field builders ──────────────────────────────────────────────────────────────

/**
 * Dựng chuỗi field COL-PER-MEMBER. field list `entries` có 2 cột con: member_picker (options = thành
 * viên THẬT của group — service nạp) + money. Immutable: copy options input sang mảng mới.
 */
export function buildColPerMemberFields(
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "title", type: "text", required: true },
    {
      key: COL_PER_MEMBER_ENTRIES_KEY,
      type: "list",
      required: true,
      columns: [
        { key: "memberId", type: "member_picker", required: true, options: [...memberOptions] },
        { key: "amountMinor", type: "money", required: true },
      ],
    },
    { key: "dueDate", type: "date", required: true },
  ];
}

/**
 * Dựng chuỗi field COL-TIERED. 2 field list liên tiếp:
 *  - `tiers`: cột con text(label) + money(amountMinor).
 *  - `memberTierMap`: cột con member_picker(memberId, options = thành viên THẬT) + single_choice(tierLabel,
 *    options = NHÃN bậc VỪA NHẬP ở field tiers — HYDRATE từ formState qua tierLabelOptions).
 *
 * 🔴 tierLabelOptions do SERVER nạp từ formState (buildTierLabelOptionsFromState) khi dựng field
 * memberTierMap — không để user tự gõ nhãn (tránh lệch nhãn). Lúc dựng field `tiers` (chưa có bậc),
 * tierLabelOptions = [] (options rỗng ⇒ single_choice reject mọi value ⇒ chưa điền được memberTierMap;
 * đúng thứ tự: phải điền tiers TRƯỚC).
 */
export function buildColTieredFields(
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
  tierLabelOptions: readonly SlotOption[],
): SlotFieldSpec[] {
  return [
    { key: "fundId", type: "fund_picker", required: true, options: [...fundOptions] },
    { key: "title", type: "text", required: true },
    {
      key: COL_TIERED_TIERS_KEY,
      type: "list",
      required: true,
      columns: [
        { key: "label", type: "text", required: true },
        { key: "amountMinor", type: "money", required: true },
      ],
    },
    {
      key: COL_TIERED_MEMBER_TIER_MAP_KEY,
      type: "list",
      required: true,
      columns: [
        { key: "memberId", type: "member_picker", required: true, options: [...memberOptions] },
        { key: "tierLabel", type: "single_choice", required: true, options: [...tierLabelOptions] },
      ],
    },
    { key: "dueDate", type: "date", required: true },
  ];
}

/**
 * 🔴 HYDRATE options-động (index §6): đọc field list `tiers` ĐÃ ĐIỀN trong formState → tập NHÃN bậc
 * (SlotOption[]) để nạp làm options cho cột con single_choice(tierLabel) của field memberTierMap. Nhãn
 * trùng bị KHỬ (Set — dedupe), nhãn rỗng bỏ qua. TẤT ĐỊNH, không throw (tiers chưa điền ⇒ mảng rỗng).
 */
export function buildTierLabelOptionsFromState(formState: FormState): SlotOption[] {
  const rows = decodeRows(formState[COL_TIERED_TIERS_KEY]);
  const labels = new Set<string>();
  for (const row of rows) {
    const label = typeof row.label === "string" ? row.label.trim() : "";
    if (label.length > 0) labels.add(label);
  }
  return [...labels].map((value) => ({ value }));
}

// ─── Shared field guards (mirror col-equal-form / collection-form-specs) ────────────

/** Ném DomainError REQUIRED_FIELD_MISSING cho field bắt buộc còn trống. */
function assertPresent(value: string | null | undefined, field: string): string {
  if (typeof value !== "string" || value.trim().length === 0) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Thiếu thông tin bắt buộc: ${field}.`,
    );
  }
  return value.trim();
}

/** fundId ∈ options (domain-level IDOR guard — quỹ ngoài danh sách user ⇒ reject). */
function validateFundId(
  formState: FormState,
  fundOptions: readonly SlotOption[],
): string {
  const fundIdRaw = assertPresent(formState.fundId, "fundId");
  const allowedFundIds = new Set(fundOptions.map((o) => o.value));
  if (!allowedFundIds.has(fundIdRaw)) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      "Quỹ được chọn không nằm trong danh sách quỹ của bạn.",
    );
  }
  return fundIdRaw;
}

/** title non-empty + ≤ trần độ dài (H1 — mirror HTTP .max(100)). */
function validateTitle(formState: FormState): string {
  const title = assertPresent(formState.title, "title");
  if (title.length > COL_PMT_TITLE_MAX_LENGTH) {
    throw new DomainError(
      "REQUIRED_FIELD_MISSING",
      `Tiêu đề quá dài (tối đa ${COL_PMT_TITLE_MAX_LENGTH} ký tự).`,
    );
  }
  return title;
}

/** dueDate ISO hợp lệ + KHÔNG quá khứ (tz group). */
function validateDueDate(formState: FormState, now: Date): string {
  const dueDateRaw = assertPresent(formState.dueDate, "dueDate");
  const dateValidation = validateFieldValue(
    { key: "dueDate", type: "date", required: true },
    dueDateRaw,
  );
  if (!dateValidation.ok) {
    throw new DomainError("COLLECTION_DUE_DATE_IN_PAST", dateValidation.message);
  }
  assertDueDateNotPast(parseIsoDateUtc(dateValidation.value), DEFAULT_GROUP_TIMEZONE, now);
  return dateValidation.value;
}

/**
 * Chốt money minor no-float + >0 + ≤ trần BIGINT cho một value ĐÃ qua regex minor (validateListField).
 * value chỉ chữ số nên BigInt() KHÔNG ném. Trả DomainError (map 400) khi ≤0 hoặc vượt trần.
 */
function assertMoneyMinorInBounds(value: string, context: string): void {
  const amount = BigInt(value);
  if (amount <= 0n) {
    throw new DomainError(
      "COLLECTION_AMOUNT_NOT_POSITIVE",
      `Số tiền (${context}) phải lớn hơn 0.`,
    );
  }
  if (amount > COL_PMT_PG_BIGINT_MAX) {
    throw new DomainError(
      "COLLECTION_AMOUNT_NOT_POSITIVE",
      `Số tiền (${context}) vượt quá giới hạn cho phép.`,
    );
  }
}

/**
 * Validate + chuẩn hoá field list qua validateListField (0249): ≥1 dòng, mỗi cột con đúng type (money
 * no-float, picker/choice ∈ options). Ném DomainError (map 400) khi vi phạm. Trả về rows đã chuẩn hoá.
 */
function validateListRows(field: SlotFieldSpec, encoded: string | null | undefined): ListRow[] {
  const validation = validateListField(field, encoded);
  if (!validation.ok) {
    throw new DomainError("REQUIRED_FIELD_MISSING", validation.message);
  }
  return decodeRows(validation.value);
}

/** Parse 'YYYY-MM-DD' (đã validate ISO) → Date UTC midnight (ổn định, không phụ thuộc tz máy). */
function parseIsoDateUtc(isoDate: string): Date {
  const [y, m, d] = isoDate.split("-").map((p) => Number.parseInt(p, 10));
  return new Date(Date.UTC(y!, (m ?? 1) - 1, d ?? 1));
}

// ─── COL-PER-MEMBER validate ────────────────────────────────────────────────────────

/**
 * Validate CẢ BỘ formState COL-PER-MEMBER → ColPerMemberPayload (fail-fast).
 * Thứ tự chốt: fundId ∈ options → title non-empty ≤100 → entries (list) ≥1 dòng, mỗi member ∈ options +
 * money minor >0 no-float ≤ trần BIGINT + KHÔNG lặp member → dueDate ISO không quá khứ.
 */
export function validateColPerMemberForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
  now: Date = new Date(),
): ColPerMemberPayload {
  const fundId = validateFundId(formState, fundOptions);
  const title = validateTitle(formState);

  const entriesField: SlotFieldSpec = {
    key: COL_PER_MEMBER_ENTRIES_KEY,
    type: "list",
    required: true,
    columns: [
      { key: "memberId", type: "member_picker", required: true, options: [...memberOptions] },
      { key: "amountMinor", type: "money", required: true },
    ],
  };
  const rows = validateListRows(entriesField, formState[COL_PER_MEMBER_ENTRIES_KEY]);

  const seenMembers = new Set<string>();
  const entries: ColPerMemberEntry[] = rows.map((row) => {
    const memberId = String(row.memberId ?? "").trim();
    const amountMinor = String(row.amountMinor ?? "").trim();
    if (seenMembers.has(memberId)) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        `Thành viên bị lặp trong danh sách per-member.`,
      );
    }
    seenMembers.add(memberId);
    assertMoneyMinorInBounds(amountMinor, "mỗi thành viên");
    return { groupMemberId: memberId, amountMinor };
  });

  const dueDate = validateDueDate(formState, now);
  return { fundId, title, entries, dueDate };
}

// ─── COL-TIERED validate ────────────────────────────────────────────────────────────

/**
 * Validate CẢ BỘ formState COL-TIERED → ColTieredPayload (fail-fast).
 * Thứ tự chốt: fundId ∈ options → title non-empty ≤100 → tiers (list) ≥1 dòng, mỗi label non-empty +
 * money minor >0 no-float ≤ trần BIGINT (KHÔNG lặp label) → memberTierMap (list) ≥1 dòng, mỗi member ∈
 * options + tierLabel ∈ tập nhãn tier (options nạp từ formState) + KHÔNG lặp member → dueDate.
 */
export function validateColTieredForm(
  formState: FormState,
  fundOptions: readonly SlotOption[],
  memberOptions: readonly SlotOption[],
  now: Date = new Date(),
): ColTieredPayload {
  const fundId = validateFundId(formState, fundOptions);
  const title = validateTitle(formState);

  // 1) tiers: list[text(label) + money]. ≥1 dòng, label non-empty, money >0 no-float, label KHÔNG lặp.
  const tiersField: SlotFieldSpec = {
    key: COL_TIERED_TIERS_KEY,
    type: "list",
    required: true,
    columns: [
      { key: "label", type: "text", required: true },
      { key: "amountMinor", type: "money", required: true },
    ],
  };
  const tierRows = validateListRows(tiersField, formState[COL_TIERED_TIERS_KEY]);

  const seenLabels = new Set<string>();
  const tiers: ColTier[] = tierRows.map((row) => {
    const label = String(row.label ?? "").trim();
    const amountMinor = String(row.amountMinor ?? "").trim();
    if (label.length === 0) {
      throw new DomainError("REQUIRED_FIELD_MISSING", "Nhãn bậc thu không được rỗng.");
    }
    if (seenLabels.has(label)) {
      throw new DomainError("REQUIRED_FIELD_MISSING", `Nhãn bậc "${label}" bị lặp.`);
    }
    seenLabels.add(label);
    assertMoneyMinorInBounds(amountMinor, `bậc ${label}`);
    return { label, amountMinor };
  });

  // 2) memberTierMap: list[member_picker + single_choice(tierLabel)]. tierLabel options = nhãn tier ĐÃ
  //    định nghĩa (hydrate từ tierRows — nguồn sự thật là tiers vừa validate). Mọi tierLabel ∈ tiers.
  const tierLabelOptions: SlotOption[] = [...seenLabels].map((value) => ({ value }));
  const mapField: SlotFieldSpec = {
    key: COL_TIERED_MEMBER_TIER_MAP_KEY,
    type: "list",
    required: true,
    columns: [
      { key: "memberId", type: "member_picker", required: true, options: [...memberOptions] },
      { key: "tierLabel", type: "single_choice", required: true, options: tierLabelOptions },
    ],
  };
  const mapRows = validateListRows(mapField, formState[COL_TIERED_MEMBER_TIER_MAP_KEY]);

  const seenMembers = new Set<string>();
  const memberTierMap: Record<string, string> = {};
  for (const row of mapRows) {
    const memberId = String(row.memberId ?? "").trim();
    const tierLabel = String(row.tierLabel ?? "").trim();
    if (seenMembers.has(memberId)) {
      throw new DomainError(
        "REQUIRED_FIELD_MISSING",
        "Thành viên bị lặp trong bảng gán bậc.",
      );
    }
    seenMembers.add(memberId);
    // Defense-in-depth: single_choice options ĐÃ chốt tierLabel ∈ tiers, chốt lại phòng options lệch.
    if (!seenLabels.has(tierLabel)) {
      throw new DomainError(
        "TIER_UNASSIGNED",
        `Bậc "${tierLabel}" không nằm trong danh sách bậc đã định nghĩa.`,
      );
    }
    memberTierMap[memberId] = tierLabel;
  }

  const dueDate = validateDueDate(formState, now);
  return { fundId, title, tiers, memberTierMap, dueDate };
}
